import React from 'react';

import { useNavigate } from 'react-router-dom';

import { Box, InputAdornment, OutlinedInput } from '@mui/material';

import { capitalize, debounce, uniqueId } from 'lodash';
import { MRT_SortingState, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useQueryClient } from '@tanstack/react-query';
import { ShowFnOutput, useModal } from 'mui-modal-provider';

import {
  UserAccessRequestResultValue,
  VennueAccessRequestItem,
  VenueListAccessRequetsFilter,
} from '../../../../graphql/API';
import useAccessRequestQuery from '../../../../hooks/reactQuery/useAccessRequestQuery';
import Icons from '../../../../themes/icons';
import ChipOptions from '../../../ChipOptions/ChipOptions';
import { BaseDialogProps } from '../../../Dialogs/Base';
import { TabOption } from '../../ListAccessRequest/components/types';
import { productRequestsTableColumns } from './columns';
import { UndoConfirmDialog } from './UndoConfirmDialog';

type AccessRequestWithReservation = VennueAccessRequestItem & { reservationID?: string | null };

const statusOptions = [
  { label: capitalize('All'), value: 'all' },
  { label: capitalize(TabOption.accepted), value: TabOption.accepted },
  { label: 'Rejected', value: TabOption.rejected },
];

export const ProductRequestsContainer = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const undoModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();

  const [filters, setFilters] = React.useState<Partial<VenueListAccessRequetsFilter>>({
    statuses: [
      UserAccessRequestResultValue.accepted,
      UserAccessRequestResultValue.declined,
      UserAccessRequestResultValue.archived,
    ],
  });

  const accessRequestsQueryResult = useAccessRequestQuery(filters);
  const { isFetching: accessRequestIsFetching, fetchNextPage: accessRequestFetchNextPage } =
    accessRequestsQueryResult;

  const [sorting, setSorting] = React.useState<MRT_SortingState>([
    {
      id: 'createdAt',
      desc: true,
    },
  ]);

  React.useEffect(() => {
    const [sort] = sorting;
    setFilters((x) => ({ ...x, sortBy: sort?.id, sortDirection: sort?.desc ? 'desc' : 'asc' }));
  }, [sorting]);

  const handleSearch = debounce((value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchText: value,
    }));
  }, 500);

  const handleChange = React.useCallback(
    (value?: string) => {
      handleSearch(value);
    },
    [handleSearch],
  );

  const handleUndo = React.useCallback(
    (row: VennueAccessRequestItem) => {
      if (!row.userID || !row.offerID) return;

      undoModalRef.current = showModal(UndoConfirmDialog, {
        row,
        modalRef: undoModalRef,
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['list-access-request'] });
        },
      });
    },
    [showModal, queryClient],
  );

  const handleView = React.useCallback(
    (row: VennueAccessRequestItem) => {
      const reservationID = (row as AccessRequestWithReservation).reservationID;
      if (reservationID) {
        navigate(`/reservations/${reservationID}`);
      } else {
        navigate('/reservations');
      }
    },
    [navigate],
  );

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !accessRequestIsFetching &&
          accessRequestsQueryResult.hasNextPage
        ) {
          accessRequestFetchNextPage();
        }
      }
    },
    [accessRequestIsFetching, accessRequestsQueryResult.hasNextPage],
  );

  const productRequestsTable = useMaterialReactTable({
    enableRowSelection: false,
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setSorting,
    muiTableHeadRowProps: {
      sx: { backgroundColor: '#fff', borderColor: '#e0e0e0', boxShadow: 'none' },
    },
    muiTableBodyCellProps: {
      sx: () => ({ borderColor: '#E0E0E0', backgroundColor: '#fff' }),
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        fontWeight: 400,
        color: '#848484',
        borderColor: custom.colors.brandLightGrey,
      }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none', backgroundColor: '#fff' } },
    muiTableContainerProps: {
      sx: {
        margin: '8px 0',
        height: '670px',
        maxHeight: '670px',
        backgroundColor: '#fff',
      },
      onScroll: (event) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    columns: productRequestsTableColumns({ onUndo: handleUndo, onView: handleView }),
    data: (accessRequestsQueryResult.data as VennueAccessRequestItem[]) || [],
    rowVirtualizerOptions: { overscan: 5 },
    state: {
      isLoading: accessRequestIsFetching || accessRequestsQueryResult.isLoading,
      sorting,
    },
    getRowId: (row) =>
      row.offerID && row.userID && row.createdAt
        ? String(`${row.userID}-${row.offerID}-${row.createdAt}`)
        : uniqueId('access-requests'),
  });

  return (
    <Box position={'relative'}>
      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <ChipOptions
          defaultValue={
            (filters.statuses?.length || 0) > 0 && (filters.statuses?.length || 0) === 3
              ? 'all'
              : (filters.statuses?.length || 0) > 0 && (filters.statuses?.length || 0) === 2
                ? TabOption.accepted
                : filters.status || UserAccessRequestResultValue.accepted
          }
          name='status'
          options={statusOptions}
          onChange={({ value: v }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              status:
                v === 'all' || v === TabOption.accepted
                  ? undefined
                  : (v as UserAccessRequestResultValue),
              statuses:
                v === 'all'
                  ? [
                      UserAccessRequestResultValue.accepted,
                      UserAccessRequestResultValue.declined,
                      UserAccessRequestResultValue.archived,
                    ]
                  : v === TabOption.accepted
                    ? [UserAccessRequestResultValue.archived, UserAccessRequestResultValue.accepted]
                    : undefined,
            }));
          }}
        />
        <OutlinedInput
          id='outlined-adornment-amount'
          placeholder='Search a member...'
          startAdornment={
            <InputAdornment position='start'>
              <Icons.SearchIcon />
            </InputAdornment>
          }
          onChange={(e) => {
            handleChange(e.target.value);
          }}
          size='small'
          sx={{ minWidth: '340px' }}
        />
      </Box>

      <MaterialReactTable table={productRequestsTable} />
    </Box>
  );
};
